import { creatorbase } from '@round/api';
import { Breadcrumb, Skeleton } from '@round/creatorbase-ui';
import { useBrand, useCampaignStats, useProject, useTeam } from '@round/creatorbase-helpers/api-hooks';
import { asMoneyWithoutZeros } from 'helpers';
import { useCurrencies } from 'contexts/OptionsContext/useCurrencies';
import cn from 'classnames';
import styles from './CampaignHeader.module.css';

type Props = {
    campaign: creatorbase.Campaign | undefined;
    onClose: () => void;
    className?: string;
};

const CampaignHeader = ({ campaign, onClose, className }: Props) => {
    const { getCurrencyById } = useCurrencies();

    const { data: project } = useProject(campaign?.project_id, { enabled: !!campaign?.project_id });
    const { data: brand } = useBrand(project?.brand_id, { enabled: !!project?.brand_id });
    const { data: team } = useTeam(project?.team_id, {}, { enabled: !!project?.team_id });

    const { data: stats, isFetching: areStatsFetching } = useCampaignStats(
        campaign?.id ? { campaign_id: campaign.id } : null,
        {
            enabled: !!campaign?.id,
        }
    );

    return (
        <div className={cn(styles.container, className)}>
            <div className={styles.topRow}>
                <Breadcrumb className={styles.breadcrumbNav}>
                    <Breadcrumb.List>
                        <Breadcrumb.Item>
                            <Breadcrumb.Link
                                render={<button type="button" onClick={onClose} className={styles.breadcrumbButton} />}
                            >
                                {project?.name}
                            </Breadcrumb.Link>
                        </Breadcrumb.Item>
                        <Breadcrumb.Separator />
                        <Breadcrumb.Item>
                            <Breadcrumb.Page>Add Creators</Breadcrumb.Page>
                        </Breadcrumb.Item>
                    </Breadcrumb.List>
                </Breadcrumb>
            </div>

            <div className={styles.campaignRow}>
                <div className={styles.row}>
                    <img src={project?.image ?? ''} alt={brand?.name ?? ''} className={styles.image} />
                    <div>
                        <p className={styles.title}>{campaign?.name}</p>
                        <p className={styles.brand}>{brand?.name}</p>
                        <p className={styles.team}>{team?.name}</p>
                    </div>
                </div>

                <dl className={styles.metricsList}>
                    <dt>Budget:</dt>
                    {areStatsFetching ? (
                        <Skeleton className={styles.metricSkeleton} />
                    ) : (
                        <dd className={styles.metric}>
                            {campaign?.currency_id
                                ? asMoneyWithoutZeros(
                                      stats?.total_budget?.[campaign.currency_id],
                                      getCurrencyById(campaign.currency_id)
                                  )
                                : '-'}
                        </dd>
                    )}

                    <dt>Posts:</dt>
                    {areStatsFetching ? (
                        <Skeleton className={styles.metricSkeleton} />
                    ) : (
                        <dd className={styles.metric}>{stats?.post_count}</dd>
                    )}
                </dl>
            </div>
        </div>
    );
};

export default CampaignHeader;
